import React, { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, ImageOff, CheckCircle2, Loader2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function InventoryCard({ item, onToggle, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const needsReorder = item.needs_reorder;
  const img = item.image_path ? fileUrl(item.image_path) : null;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onToggle(item);
      setOpen(false);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <motion.button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        whileTap={{ scale: 0.96 }}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 22 }}
        data-testid={`item-card-${item.id}`}
        data-reorder={needsReorder ? "true" : "false"}
        disabled={disabled}
        className={`group relative aspect-square w-full overflow-hidden rounded-2xl border bg-white text-left shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F7C8E0] ${
          needsReorder
            ? "border-[#FF7878] ring-4 ring-[#FF7878]/40 bg-[#FFF0F0]"
            : "border-[#EADBCE] hover:shadow-md"
        } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {/* Image */}
        <div className="relative h-[70%] w-full overflow-hidden bg-[#FCF9F2]">
          {img ? (
            <img
              src={img}
              alt={item.name}
              className={`h-full w-full object-cover transition-all duration-300 ${
                needsReorder ? "scale-105 saturate-50 brightness-90" : "group-hover:scale-105"
              }`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-[#EADBCE]">
              <ImageOff className="w-10 h-10" />
            </div>
          )}

          {needsReorder && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/15">
              <div
                className="px-3 py-1.5 rounded-full bg-[#FF5C5C] text-white text-xs font-extrabold tracking-wide shadow-lg flex items-center gap-1"
                data-testid={`reorder-badge-${item.id}`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                REORDENAR
              </div>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="h-[30%] px-3 py-2 flex flex-col justify-center">
          <div className="flex items-start justify-between gap-2">
            <span className="font-display font-bold leading-tight text-[#4A3B32] line-clamp-2 text-sm sm:text-base">
              {item.name}
            </span>
            {!needsReorder && (
              <CheckCircle2 className="shrink-0 w-4 h-4 text-[#A7E4D3]" />
            )}
          </div>
          {needsReorder && item.marked_by && (
            <span className="text-[10px] text-[#8A796F] truncate mt-0.5">
              por {item.marked_by.name}
            </span>
          )}
        </div>
      </motion.button>

      <AlertDialog open={open} onOpenChange={(v) => !confirming && setOpen(v)}>
        <AlertDialogContent
          className="rounded-3xl border-[#EADBCE] bg-white max-w-sm p-0 overflow-hidden"
          data-testid={`confirm-dialog-${item.id}`}
        >
          {/* Visual preview */}
          <div className="relative aspect-square w-full bg-[#FCF9F2]">
            {img ? (
              <img src={img} alt={item.name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-[#EADBCE]">
                <ImageOff className="w-12 h-12" />
              </div>
            )}
            <div
              className={`absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wide ${
                needsReorder
                  ? "bg-[#A7E4D3] text-[#1F5C4A]"
                  : "bg-[#FF7878] text-white"
              }`}
            >
              {needsReorder ? "MARCAR REORDENADO" : "MARCAR REORDENAR"}
            </div>
          </div>

          <div className="p-6 pt-4">
            <AlertDialogHeader className="space-y-2 text-left">
              <AlertDialogTitle className="font-display text-2xl text-[#4A3B32]">
                {item.name}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#8A796F] text-sm leading-relaxed">
                {needsReorder
                  ? "¿Confirmas que ya reordenaste este producto? Se quitará de la lista de pendientes."
                  : "¿Confirmas que se está acabando? Se avisará al Gerente para reordenarlo."}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter className="mt-6 flex-row gap-2 sm:gap-2">
              <AlertDialogCancel
                disabled={confirming}
                data-testid={`confirm-cancel-${item.id}`}
                className="flex-1 rounded-full border-[#EADBCE] bg-white text-[#4A3B32] hover:bg-[#FCF9F2] font-bold mt-0"
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirm();
                }}
                disabled={confirming}
                data-testid={`confirm-accept-${item.id}`}
                className={`flex-1 rounded-full font-bold text-white ${
                  needsReorder
                    ? "bg-[#A7E4D3] text-[#1F5C4A] hover:bg-[#8FD9C3]"
                    : "bg-[#FF7878] hover:bg-[#FF5C5C]"
                }`}
              >
                {confirming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : needsReorder ? (
                  "Sí, ya lo reordené"
                ) : (
                  "Sí, reordenar"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
